import { useState, useEffect } from "react";
import { useQuery, useMutation, QueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { X, Plus } from "lucide-react";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { TeamMember, ToastFn } from "./_shared";



// ── Team Member Rates (Task #2783) ──────────────────────────────────────────
// Owner-only editor in the Pay tab. For each active team member (owner
// included) the owner can set an hourly rate, a default trip/travel fee, and a
// named flat-rate catalog. These pre-fill the accept-with-estimate dialog when
// a task assigned to that member is accepted.
export function TeamMemberRateRow({ member, vendorId, queryClient, toast, onSaved }: {
  member: TeamMember;
  vendorId: number;
  queryClient: QueryClient;
  toast: ToastFn;
  onSaved?: () => void;
}) {
  const [hourlyInput, setHourlyInput] = useState<string>(member.hourlyRateCents != null ? String(member.hourlyRateCents / 100) : "");
  const [travelInput, setTravelInput] = useState<string>(member.defaultTravelFeeCents != null ? String(member.defaultTravelFeeCents / 100) : "");
  const [offerings, setOfferings] = useState<Array<{ name: string; priceCents: number }>>(
    Array.isArray(member.flatRateOfferings) ? member.flatRateOfferings : [],
  );
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");

  useEffect(() => {
    setHourlyInput(member.hourlyRateCents != null ? String(member.hourlyRateCents / 100) : "");
    setTravelInput(member.defaultTravelFeeCents != null ? String(member.defaultTravelFeeCents / 100) : "");
    setOfferings(Array.isArray(member.flatRateOfferings) ? member.flatRateOfferings : []);
  }, [member.hourlyRateCents, member.defaultTravelFeeCents, member.flatRateOfferings]);

  const saveMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: (payload: { hourlyRateCents: number | null; defaultTravelFeeCents: number | null; flatRateOfferings: Array<{ name: string; priceCents: number }> }) =>
      apiRequest("PATCH", `/api/vendor-portal/team/${member.id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor", vendorId, "team"] });
      toast({ title: "Rates saved", description: `${member.firstName}'s rates were updated.` });
      onSaved?.();
    },
    onError: (e: Error) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to save rates"), variant: "destructive" }),
  });

  const handleSave = () => {
    const hourlyRateCents = hourlyInput ? Math.round(parseFloat(hourlyInput) * 100) : null;
    const defaultTravelFeeCents = travelInput ? Math.round(parseFloat(travelInput) * 100) : null;
    saveMutation.mutate({ hourlyRateCents, defaultTravelFeeCents, flatRateOfferings: offerings });
  };

  return (
    <Card data-testid={`card-member-rates-${member.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 text-sm flex-1">{member.firstName} {member.lastName}</p>
          {member.role === "owner" && <Badge variant="default" className="text-xs">Owner</Badge>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor={`input-member-hourly-${member.id}`} className="text-xs">Hourly Rate ($)</Label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                id={`input-member-hourly-${member.id}`}
                type="number"
                min="0"
                step="0.01"
                className="pl-5 h-8 text-sm"
                placeholder="0.00"
                value={hourlyInput}
                onChange={e => setHourlyInput(e.target.value)}
                data-testid={`input-member-hourly-${member.id}`}
              />
            </div>
          </div>
          <div>
            <Label htmlFor={`input-member-travel-${member.id}`} className="text-xs">Trip Fee ($)</Label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                id={`input-member-travel-${member.id}`}
                type="number"
                min="0"
                step="0.01"
                className="pl-5 h-8 text-sm"
                placeholder="0.00"
                value={travelInput}
                onChange={e => setTravelInput(e.target.value)}
                data-testid={`input-member-travel-${member.id}`}
              />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Flat-rate Offerings</Label>
          {offerings.map((offering, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`row-member-offering-${member.id}-${idx}`}>
              <span className="flex-1 truncate">{offering.name}</span>
              <span className="text-gray-600 font-medium">${(offering.priceCents / 100).toFixed(2)}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                onClick={() => setOfferings(prev => prev.filter((_, i) => i !== idx))}
                data-testid={`button-remove-member-offering-${member.id}-${idx}`}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Service name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="flex-1 h-8 text-sm"
              data-testid={`input-member-offering-name-${member.id}`}
            />
            <div className="relative w-24">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={newPrice}
                onChange={e => setNewPrice(e.target.value)}
                className="pl-5 h-8 text-sm"
                data-testid={`input-member-offering-price-${member.id}`}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              disabled={!newName.trim() || !newPrice}
              onClick={() => {
                const priceCents = Math.round(parseFloat(newPrice) * 100);
                if (isNaN(priceCents) || priceCents <= 0) return;
                setOfferings(prev => [...prev, { name: newName.trim(), priceCents }]);
                setNewName("");
                setNewPrice("");
              }}
              data-testid={`button-add-member-offering-${member.id}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <Button
          size="sm"
          className="w-full"
          onClick={handleSave}
          disabled={saveMutation.isPending}
          data-testid={`button-save-member-rates-${member.id}`}
        >
          {saveMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}


export function TeamMemberRatesSection({ vendorId, queryClient, toast }: {
  vendorId: number;
  queryClient: QueryClient;
  toast: ToastFn;
}) {
  const { data: members = [], isLoading } = useQuery<TeamMember[]>({
    queryKey: ["vendor", vendorId, "team"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/team", { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
  });

  const activeMembers = members.filter(m => m.status !== "removed");

  return (
    <Card data-testid="card-team-member-rates">
      <CardHeader>
        <CardTitle className="text-sm">Team Member Rates</CardTitle>
        <p className="text-xs text-gray-500">Set per-member hourly rates, trip fees, and flat-rate services. These pre-fill the estimate dialog when a job assigned to that member is accepted. Members without rates use the defaults above.</p>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        {isLoading ? (
          <p className="text-sm text-gray-400 text-center py-4">Loading team...</p>
        ) : activeMembers.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No team members yet.</p>
        ) : (
          activeMembers.map(member => (
            <TeamMemberRateRow key={member.id} member={member} vendorId={vendorId} queryClient={queryClient} toast={toast} />
          ))
        )}
      </CardContent>
    </Card>
  );
}
